import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Icon, type IconName } from '../shell/Icon.tsx';
import styles from './Button.module.css';

/* ==========================================================================
   Button.

   One primary per surface. Secondary verbs are outlined, never filled.
   Destructive is red and last. Icon-only buttons must carry `label`, which
   becomes both the accessible name and the tooltip.
   ========================================================================== */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

interface ButtonProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  variant?: ButtonVariant | undefined;
  size?: ButtonSize | undefined;
  icon?: IconName | undefined;
  /** Icon after the label — chevrons, external-link. */
  iconEnd?: IconName | undefined;
  /** Keyboard shortcut, printed in the affordance. `⌘S`. */
  kbd?: string | undefined;
  /** Count carried by the verb itself: `Visualize 3`. */
  badge?: number | string | undefined;
  block?: boolean | undefined;
  /** Marks a ghost toggle as the current one. */
  active?: boolean | undefined;
  children?: ReactNode;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  icon,
  iconEnd,
  kbd,
  badge,
  block = false,
  active = false,
  children,
  type = 'button',
  ...rest
}: ButtonProps) {
  const iconOnly = children === undefined && icon !== undefined;
  return (
    <button
      {...rest}
      type={type}
      className={styles.btn}
      data-variant={variant}
      data-size={size}
      data-block={block}
      data-active={active}
      data-icon-only={iconOnly}
    >
      {icon !== undefined && <Icon name={icon} size={size === 'sm' ? 14 : 16} />}
      {children}
      {badge !== undefined && <span className={styles.badge}>{badge}</span>}
      {kbd !== undefined && <span className={styles.kbd}>{kbd}</span>}
      {iconEnd !== undefined && <Icon name={iconEnd} size={size === 'sm' ? 14 : 16} />}
    </button>
  );
}

/**
 * Icon-only control. `label` is mandatory — it is the accessible name and the
 * title, because an icon with no text has to say what it does out loud.
 */
export function IconButton({
  icon,
  label,
  variant = 'ghost',
  size = 'md',
  active = false,
  ...rest
}: Omit<ButtonProps, 'children' | 'icon'> & { icon: IconName; label: string }) {
  return (
    <button
      {...rest}
      type={rest.type ?? 'button'}
      className={styles.btn}
      data-variant={variant}
      data-size={size}
      data-active={active}
      data-icon-only="true"
      aria-label={label}
      title={label}
    >
      <Icon name={icon} size={size === 'sm' ? 14 : 16} />
    </button>
  );
}

/** A row of verbs that belong together. Destructive goes last. */
export function ButtonGroup({ children }: { children: ReactNode }) {
  return <div className={styles.group}>{children}</div>;
}
